'use client'

import { useContext, useEffect } from 'react'
import { useIntl } from 'react-intl'
import {
  Bubble,
  Button,
  Cell,
  Dialog,
  Divider,
  Icon,
  Image,
  InfoBanner,
  Label,
  Navigation,
  Note,
  SelectionGroup,
  Spacer,
  Text,
} from '@vinted/web-ui'
import { Clock16, InfoCircle16, LocationPin16, Mailbox16, X24 } from '@vinted/monochrome-icons'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/shared/assets'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  RequestReturnDetailsModel,
  RequestReturnDetailsPaymentOptionModel,
  ComplaintModel,
  ReturnShippingMethodPayingSide,
  clickEvent,
  formatCurrencyAmount,
} from '@marketplace-web/complaints/complaints-data'

import {
  FieldName,
  PhoneRequirement,
  ShippingContactType,
} from '@marketplace-web/shipping/shipping-contact-data'

import ShipmentItems from '../ShipmentItems'
import ComplaintOrderItems from '../ComplaintOrderItems'
import RequestReturnDetailsContext from '../../RequestReturn/RequestReturnProvider/RequestReturnContext'
import Address from '../../Address'
import ShippingContact from '../../ShippingContact'

const CARRIER_ICON_PLACEHOLDER = 'shipping/carrier-placeholder.svg'

type Props = {
  isLoading: boolean
  returnShipment: RequestReturnDetailsModel
  complaint?: ComplaintModel | null
  transactionId?: number
  conversationTransactionIsBundle?: boolean
  onClose: () => void
  onConfirm: () => void
  onAddressModalOpen: () => void
  onShippingContactClick: () => void
  onShippingDiscountClick: () => void
  onTotalRefundClick: () => void
}

const Intro = ({
  isLoading,
  returnShipment,
  complaint,
  transactionId,
  conversationTransactionIsBundle,
  onClose,
  onConfirm,
  onAddressModalOpen,
  onShippingContactClick,
  onShippingDiscountClick,
  onTotalRefundClick,
}: Props) => {
  const translate = useTranslate('conversation.actions.request_return_modal')
  const { track } = useTracking()
  const asset = useAsset()
  const { locale } = useIntl()

  const {
    selectedPaymentSide,
    updateSelectedPaymentSide,
    addressError,
    shippingContactError,
    setAddressError,
    setShippingContactError,
  } = useContext(RequestReturnDetailsContext)

  const isSellerPaying = selectedPaymentSide === ReturnShippingMethodPayingSide.Seller

  const { userAddress, phoneNumber, photoUrl, phoneNumberRequirement } = returnShipment

  const isUnifiedComplaintFlow = !!complaint?.complaintOrderItems?.length
  const { showAllItemsReturnBanner } = complaint || {}

  useEffect(() => {
    if (!phoneNumber) return

    setShippingContactError(null)
  }, [phoneNumber, setShippingContactError])

  useEffect(() => {
    if (!userAddress) return

    setAddressError(null)
  }, [userAddress, setAddressError])

  const renderItemVerificationInfoBanner = () => {
    if (!returnShipment.withAuthenticityCheck) return null

    return <InfoBanner body={<Text as="span" text={translate('offline_verification')} />} />
  }

  const renderElectronicsVerificationInfoBanner = () => {
    if (!returnShipment.withElectronicsVerification) return null

    return <InfoBanner body={<Text as="span" text={translate('electronics_verification')} />} />
  }

  const renderShowAllItemsReturnInfoBanner = () => {
    if (!showAllItemsReturnBanner) return null

    return <InfoBanner body={<Text as="span" text={translate('all_items_return_banner')} />} />
  }

  const trackSubmitClick = () => {
    const targetDetails = JSON.stringify({
      transaction_id: transactionId,
    })

    track(
      clickEvent({
        screen: 'request_item_return',
        target: 'confirm',
        targetDetails,
      }),
    )
  }

  const handleConfirm = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (isLoading) {
      event.preventDefault()

      return
    }

    trackSubmitClick()

    let isValid = true

    if (!userAddress) {
      setAddressError(translate('errors.address'))
      isValid = false
    }

    if (phoneNumberRequirement === PhoneRequirement.Mandatory && !phoneNumber) {
      setShippingContactError({
        field: FieldName.ShippingContact,
        value: translate('errors.shipping_contact'),
      })
      isValid = false
    }

    if (isValid) {
      onConfirm()
    }
  }

  const closeDialog = () => {
    setAddressError(null)
    setShippingContactError(null)
    onClose()
  }

  const renderModalNavigation = () => (
    <>
      <Navigation
        right={
          <Button
            styling={Button.Styling.Flat}
            iconName={X24}
            inline
            onClick={closeDialog}
            testId="request-return-modal-close"
            aria-label={translate('a11y.close')}
          />
        }
        body={<Text as="h2" type={Text.Type.Title} text={translate('title')} />}
      />
      <Divider />
    </>
  )

  const renderFullComplaintOrder = () => {
    if (isUnifiedComplaintFlow) return null

    return <ShipmentItems transactionId={transactionId} />
  }

  const renderUnifiedComplaintOrderItems = () => {
    if (!isUnifiedComplaintFlow) return null

    return (
      <ComplaintOrderItems
        isBuyerSide={false}
        complaintOrderItems={complaint?.complaintOrderItems || []}
      />
    )
  }

  const renderFullBundleNote = () => {
    if (!conversationTransactionIsBundle || isUnifiedComplaintFlow) return null

    return <Note text={translate('bundle_items_note')} />
  }

  const renderHeader = () => {
    const imageSrc = asset('images/free-return/return-illustration.svg', {
      theme: { dark: 'images/free-return/return-illustration-dark.svg' },
    })

    return (
      <Cell>
        <div className="u-text-center">
          <Image src={imageSrc} size={Image.Size.X2Large} />
          <Spacer />
          <Text as="h1" type={Text.Type.Heading} text={translate('header.title')} />
        </div>
        <Spacer size={Spacer.Size.Large} />
        <Text as="span" html type={Text.Type.Body} text={returnShipment.explanationNote} />
      </Cell>
    )
  }

  const renderAddress = () => {
    return (
      <>
        <Label type={Label.Type.Stacked} text={translate('return_address')} />
        <Address
          address={userAddress}
          photo={
            <Image
              src={photoUrl || asset(`no-photo/${EMPTY_USER_IMAGE_NAME}`)}
              size={Image.Size.Regular}
              styling={Image.Styling.Circle}
            />
          }
          handleEditAddressOpen={onAddressModalOpen}
          error={addressError}
        />
        <Divider />
      </>
    )
  }

  const renderShippingContact = () => {
    if (!phoneNumberRequirement || !transactionId) return null

    return (
      <>
        <ShippingContact
          type={ShippingContactType.ReturnLabel}
          translationPrefix="shipping_contact.seller.return_label"
          phoneNumber={phoneNumber}
          phoneRequirement={phoneNumberRequirement}
          transactionId={transactionId}
          isDisabled={phoneNumberRequirement === PhoneRequirement.Disabled}
          isCompleted={!!phoneNumber}
          error={shippingContactError}
          onClick={onShippingContactClick}
        />
        <Divider />
      </>
    )
  }

  const renderShippingPointSection = (
    iconName: React.ComponentProps<typeof Icon>['name'],
    text: string,
  ) => {
    if (!text) return null

    return (
      <div className="u-flexbox u-gap-medium">
        <Icon
          name={iconName}
          color={Icon.Color.GreyscaleLevel2}
          testId={`request-return-modal-shipping-point-${iconName.Title}-icon`}
        />
        <Text as="h3" text={text} type={Text.Type.Subtitle} />
      </div>
    )
  }

  const renderShippingPointInfo = () => {
    if (!returnShipment.pickupPointDetails) return null

    const { pickupPointName, pickupPointAddress, valueProposition } =
      returnShipment.pickupPointDetails

    return (
      <SeparatedList separator={<Spacer size={Spacer.Size.Small} />}>
        {renderShippingPointSection(Mailbox16, pickupPointName)}
        {renderShippingPointSection(LocationPin16, pickupPointAddress)}
        {renderShippingPointSection(Clock16, valueProposition)}
      </SeparatedList>
    )
  }

  const renderCarrierSection = () => {
    if (!returnShipment.pickupPointDetails) return null

    const { carrierName, carrierLogoUrl, discount } = returnShipment.pickupPointDetails

    const body = discount?.percent ? (
      <Text
        as="h2"
        text={
          <>
            {carrierName}
            <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
            <Text
              as="span"
              text={`(-${discount.percent}%)`}
              theme="warning"
              testId="request-return-discount-percent"
            />
          </>
        }
        type={Text.Type.Title}
        testId="request-return-carrier-title"
      />
    ) : (
      <div className="u-flexbox u-align-items-center u-gap-medium">
        <Text
          as="h2"
          text={carrierName}
          type={Text.Type.Title}
          testId="request-return-carrier-title"
        />
      </div>
    )

    return (
      <Cell
        styling={Cell.Styling.Tight}
        theme="transparent"
        prefix={
          <Image
            src={carrierLogoUrl}
            fallbackSrc={asset(CARRIER_ICON_PLACEHOLDER)}
            size={Image.Size.Regular}
            alt=""
          />
        }
        body={body}
      />
    )
  }

  const handleShippingDiscountInfoClick = () => {
    track(
      clickEvent({
        screen: 'request_item_return',
        target: 'shipping_discount_details',
        targetDetails: JSON.stringify({
          transaction_id: transactionId,
        }),
      }),
    )

    onShippingDiscountClick()
  }

  const renderPrice = () => {
    if (!returnShipment.pickupPointDetails) return null

    const { price, priceBeforeDiscount } = returnShipment.pickupPointDetails

    if (!priceBeforeDiscount)
      return (
        <div className="u-flexbox u-align-items-center">
          <Text
            as="h2"
            text={`${formatCurrencyAmount(price, locale)}`}
            type={Text.Type.Title}
            bold
          />
        </div>
      )

    return (
      <div className="u-flexbox u-align-items-center">
        <Text
          as="span"
          text={`${formatCurrencyAmount(price, locale)}`}
          type={Text.Type.Body}
          bold
          testId="request-return-price-after-discount"
        />
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
        <Text
          as="h3"
          text={formatCurrencyAmount(priceBeforeDiscount, locale)}
          type={Text.Type.Subtitle}
          strikethrough
          testId="request-return-price-before-discount"
        />
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
        <button
          type="button"
          className="u-flexbox u-align-items-center"
          onClick={handleShippingDiscountInfoClick}
          aria-label={translate('a11y.delivery_discount_info_icon')}
        >
          <Icon
            name={InfoCircle16}
            color={Icon.Color.GreyscaleLevel2}
            testId="request-return-discount-info-icon"
          />
        </button>
      </div>
    )
  }

  const renderDeliveryDetails = () => {
    if (!returnShipment.pickupPointDetails) return null

    return (
      <>
        <Label type={Label.Type.Stacked} text={translate('delivery_details')} />
        <Cell testId="return-shipment-delivery-details">
          <Bubble styling={Bubble.Styling.Tight}>
            <Cell
              theme="transparent"
              title={renderCarrierSection()}
              body={
                <>
                  <Spacer />
                  {renderPrice()}
                  <Spacer size={Spacer.Size.Large} />
                  {renderShippingPointInfo()}
                </>
              }
            />
          </Bubble>
        </Cell>
      </>
    )
  }

  const renderConfirmButton = () => {
    const buttonText = isSellerPaying ? translate('actions.continue') : translate('actions.confirm')

    return (
      <Cell>
        <Button
          styling={Button.Styling.Filled}
          text={buttonText}
          isLoading={isLoading}
          disabled={isLoading}
          onClick={handleConfirm}
          testId="request-return-modal-confirm-button"
        />
      </Cell>
    )
  }

  const selectPaymentOptionSide = (code: ReturnShippingMethodPayingSide) => () => {
    updateSelectedPaymentSide(code)
  }

  const renderPayerSelection = (payerSelection: RequestReturnDetailsPaymentOptionModel) => {
    const { code, title, subtitle } = payerSelection

    return (
      <SelectionGroup.SelectionItem
        key={code}
        title={
          <>
            <Text as="h2" type={Text.Type.Title} text={title} />
            <Spacer size={Spacer.Size.Large} />
          </>
        }
        body={<Text as="p" type={Text.Type.Subtitle} text={subtitle} />}
        onClick={selectPaymentOptionSide(code)}
        isSelected={code === selectedPaymentSide}
        testId={`intro-${code}-radio`}
      />
    )
  }

  const renderPaymentOptionsSelection = () => {
    const { payerSelection } = returnShipment

    if (!payerSelection?.length) return null

    return (
      <>
        <Label type={Label.Type.Stacked} text={translate('return_payment_label')} />
        <SelectionGroup layout={SelectionGroup.Layout.Default}>
          {payerSelection?.map(renderPayerSelection)}
        </SelectionGroup>
      </>
    )
  }

  const renderRefundInfoIcon = () => {
    if (!isUnifiedComplaintFlow) return null
    if (showAllItemsReturnBanner) return null

    return (
      <>
        <Spacer orientation={Spacer.Orientation.Vertical} />
        <button
          className="u-flexbox u-align-items-center"
          onClick={onTotalRefundClick}
          type="button"
          title={translate('a11y.refund_info_icon')}
          aria-label={translate('a11y.refund_info_icon')}
        >
          <Icon name={InfoCircle16} color={Icon.Color.GreyscaleLevel2} />
        </button>
      </>
    )
  }

  const renderReturnPriceInformation = () => {
    if (!isUnifiedComplaintFlow || !complaint?.estimatedRelease) return null

    return (
      <Cell divider>
        <Cell
          styling={Cell.Styling.Tight}
          title={<Text as="span" text={translate('order_price')} />}
          suffix={formatCurrencyAmount(complaint.estimatedRelease.totalPrice, locale)}
        />
        <Cell
          styling={Cell.Styling.Tight}
          title={
            <div className="u-flexbox">
              <Text as="span" text={translate('refund_amount')} />
              {renderRefundInfoIcon()}
            </div>
          }
          suffix={formatCurrencyAmount(complaint.estimatedRelease.totalEstimatedRefund, locale, {
            formatAsNegative: true,
          })}
        />
        <Spacer size={Spacer.Size.Medium} />
        <Cell
          styling={Cell.Styling.Tight}
          title={translate('total_earned')}
          suffix={
            <Text
              as="h2"
              type={Text.Type.Title}
              text={formatCurrencyAmount(complaint.estimatedRelease.totalEstimatedRelease, locale)}
            />
          }
        />
      </Cell>
    )
  }

  return (
    <Dialog show hasScrollableContent testId="request-return-modal">
      <div className="u-fill-width">
        {renderModalNavigation()}
        <div className="modal-contained-content">
          {renderFullComplaintOrder()}
          {renderFullBundleNote()}
          {renderItemVerificationInfoBanner()}
          {renderElectronicsVerificationInfoBanner()}
          {renderHeader()}
          {renderShowAllItemsReturnInfoBanner()}
          {renderUnifiedComplaintOrderItems()}
          {renderAddress()}
          {renderShippingContact()}
          {renderPaymentOptionsSelection()}
          {isSellerPaying && renderDeliveryDetails()}
          {renderReturnPriceInformation()}
          {renderConfirmButton()}
        </div>
      </div>
    </Dialog>
  )
}

export default Intro
